import { isAfter, isValid, parse } from 'date-fns'
import { TEXT_LENGTH, TextLength } from 'src/shared/constants/constants'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { isValidPhoneNumber } from 'libphonenumber-js'

const VALID = undefined

type ValidationSuccess = typeof VALID

export type ValidationError = {
  errorKey: string
  params?: (string | number)[]
}

export type ValidationOutput = ValidationError | ValidationSuccess

type ValidatorFunc = (value: string & number) => ValidationOutput
type ValidatorArgs = Array<string | number>
type ComposeValidatorsFuncReturn = (value: string) => ValidatorFunc

export const composeValidators =
  (...validators: Array<ValidatorFunc>): ComposeValidatorsFuncReturn =>
  (...args: ValidatorArgs) =>
    validators.reduce((error: any, validator: any) => error || validator(...args), undefined)

export const required =
  (errorKey = 'validation.required') =>
  (value: any): ValidationOutput => {
    const isArray = Array.isArray(value)
    const isBoolean = typeof value === 'boolean'

    if (value && !isArray && !isBoolean) {
      return VALID
    }

    if (value && isArray) {
      return value.length > 0 ? VALID : { errorKey }
    }

    if (isBoolean) {
      return VALID
    }

    return { errorKey }
  }

export const validPositiveNumber =
  (errorKey = 'validation.positiveNumber', allowZero: boolean = false) =>
  (value: string | number): ValidationOutput => {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    // Check if it's a valid number and positive
    if (!isNaN(numericValue) && (allowZero ? numericValue >= 0 : numericValue > 0)) {
      return VALID
    }

    return {
      errorKey,
    }
  }

/**
 * A regular expression that matches valid e-mail addresses.
 *
 * At a high level, this regexp matches e-mail addresses of the format `local-part@tld`, where:
 * - `local-part` consists of one or more of the allowed characters (alphanumeric and some
 *   punctuation symbols).
 * - `local-part` cannot begin or end with a period (`.`).
 * - `local-part` cannot be longer than 64 characters.
 * - `tld` consists of one or more `labels` separated by periods (`.`). For example `localhost` or
 *   `foo.com`.
 * - A `label` consists of one or more of the allowed characters (alphanumeric, dashes (`-`) and
 *   periods (`.`)).
 * - A `label` cannot begin or end with a dash (`-`) or a period (`.`).
 * - A `label` cannot be longer than 63 characters.
 * - The whole address cannot be longer than 254 characters.
 *
 * ## Implementation background
 *
 * This regexp was ported over from AngularJS (see there for git history):
 * https://github.com/angular/angular.js/blob/c133ef836/src/ng/directive/input.js#L27
 * It is based on the
 * [WHATWG version](https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address) with
 * some enhancements to incorporate more RFC rules (such as rules related to domain names and the
 * lengths of different parts of the address). The main differences from the WHATWG version are:
 *   - Disallow `local-part` to begin or end with a period (`.`).
 *   - Disallow `local-part` length to exceed 64 characters.
 *   - Disallow total address length to exceed 254 characters.
 *
 * See [this commit](https://github.com/angular/angular.js/commit/f3f5cf72e) for more details.
 */
const emailRegex =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export const validEmail =
  (errorKey = 'validation.email') =>
  (value: string): ValidationOutput => {
    if (emailRegex.test(value)) {
      return VALID
    }

    return {
      errorKey,
    }
  }

export const validNotRequiredEmail =
  (errorKey = 'validation.email') =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    if (emailRegex.test(value)) {
      return VALID
    } else {
      return {
        errorKey,
      }
    }
  }

function countDigitsInString(stringValue: string) {
  return (stringValue.match(/\d/g) || []).length
}

export const validNotRequiredPhoneNumber =
  (errorKey = 'validation.phone') =>
  (value?: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    // checks if the passed value is a valid local number in CH, DE, FR and IT.
    // this in practice includes a lot of local phone numbers from other countries as well.
    //
    // if not, the last clause validates international numbers, but these numbers must include country codes
    const isValid =
      isValidPhoneNumber(value, 'CH') ||
      isValidPhoneNumber(value, 'DE') ||
      isValidPhoneNumber(value, 'FR') ||
      isValidPhoneNumber(value, 'IT') ||
      isValidPhoneNumber(value)

    if (!isValid) return { errorKey }

    const digitCount = countDigitsInString(value)
    return digitCount >= 10 && digitCount <= 13 ? VALID : { errorKey }
  }

export const cleanupWhitespaceFromPhoneNumber = (phone: string) => {
  return phone.trim().replace(/\s+/g, ' ')
}

const postalCodeRegex = /^(\d{4})$/

export const validNotRequiredPostalCode =
  (errorKey = 'validation.postalcode') =>
  (value?: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    if (postalCodeRegex.test(value)) {
      return VALID
    } else {
      return {
        errorKey,
      }
    }
  }

const websiteUrlRegex = /^(https?:\/\/)?([\w-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/

export const validNotRequiredWebsiteUrl =
  (errorKey = 'validation.websiteurl') =>
  (value?: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    if (websiteUrlRegex.test(value)) {
      return VALID
    } else {
      return {
        errorKey,
      }
    }
  }

export const validDateInRange =
  (
    dateMin?: Date,
    dateMax?: Date,
    errorKey = 'validation.date.invalid',
    errorKeyInvalidRange = 'validation.date.invalid.range',
  ) =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    if (dateMin === undefined && dateMax === undefined) {
      return validDate(errorKey)(value)
    }

    const valueAsDate = parse(value, DateUtils.DATE_FORMAT, 0)

    const rangeStartDate = dateMin || DateUtils.MIN_DATE
    const rangeEndDate = dateMax || DateUtils.MAX_DATE

    if (
      isValid(valueAsDate) &&
      (DateUtils.before(valueAsDate, rangeStartDate) || DateUtils.after(valueAsDate, rangeEndDate))
    ) {
      const validationParams = []

      if (dateMin !== undefined) {
        validationParams.push(DateUtils.formatDate(dateMin))
      }

      if (dateMax !== undefined) {
        validationParams.push(DateUtils.formatDate(dateMax))
      }

      return {
        errorKey: errorKeyInvalidRange,
        params: validationParams,
      }
    }
    return isValid(valueAsDate) ? VALID : { errorKey }
  }

export const validDate =
  (errorKey = 'validation.date.invalid') =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }

    const valueAsDate = parse(value, DateUtils.DATE_FORMAT, 0)
    // by default Material UI picker has a default `minDate` and `maxDate` that it will use to both
    // control which years are available in the picker, but also color the input with error color if it is invalid
    // that's why we need to additionally validate that the 'year' is in acceptable range
    // https://github.com/mui-org/material-ui/blob/a0cccf538eab4ea50b0601e7296ac583f16004f9/packages/material-ui-lab/src/LocalizationProvider/LocalizationProvider.tsx#L51
    if (
      isValid(valueAsDate) &&
      (DateUtils.before(valueAsDate, DateUtils.MIN_DATE) || DateUtils.after(valueAsDate, DateUtils.MAX_DATE))
    ) {
      return {
        errorKey: 'validation.date.invalid.range',
        params: [DateUtils.formatDate(DateUtils.MIN_DATE), DateUtils.formatDate(DateUtils.MAX_DATE)],
      }
    }
    return isValid(valueAsDate) ? VALID : { errorKey }
  }

export const dateIsAfterToday =
  (errorKey = 'validation.date.after.today') =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const valueAsDate = parse(value, DateUtils.DATE_FORMAT, 0)
    const today = new Date()
    return isAfter(valueAsDate, today) ? VALID : { errorKey }
  }

export const validTime =
  (errorKey = 'validation.time.valid') =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const valueAsDate = parse(value, 'dd.MM.yyyy HH:mm', 0)
    return isValid(valueAsDate) ? VALID : { errorKey }
  }

export const maxChar =
  (maxLength: TextLength | number = TEXT_LENGTH.M) =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const errorKey = `validation.max.char`
    const allowedChars = typeof maxLength === 'number' ? maxLength : maxLength.value
    const messageValue =
      typeof maxLength === 'number' ? maxLength : maxLength.messageValue ? maxLength.messageValue : allowedChars

    if (value.length > allowedChars) {
      return { errorKey, params: [messageValue] }
    } else {
      return VALID
    }
  }

export const minChar =
  (minLength = 0) =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const errorKey = `validation.min.char`

    if (value.length < minLength) {
      return { errorKey, params: [minLength] }
    } else {
      return VALID
    }
  }

export const validPattern =
  (pattern: RegExp, errorMessageKey?: string) =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const defaultErrorKey = `validation.pattern`

    const isValid = pattern.test(value)
    if (!isValid) {
      if (errorMessageKey) {
        return { errorKey: errorMessageKey }
      } else {
        return { errorKey: defaultErrorKey, params: [`${pattern}`] }
      }
    } else {
      return VALID
    }
  }

export const validYear =
  (errorKey = 'validation.reporting.filter.year.invalid.range') =>
  (value: string): ValidationOutput => {
    if (!value) {
      return VALID
    }
    const valueAsDate = parse(value, DateUtils.DATE_FORMAT, 0)

    if (
      !isValid(valueAsDate) ||
      (isValid(valueAsDate) &&
        (DateUtils.before(valueAsDate, DateUtils.MIN_DATE) || DateUtils.after(valueAsDate, DateUtils.MAX_DATE)))
    ) {
      return {
        errorKey: errorKey,
        params: [DateUtils.MIN_DATE.getFullYear(), DateUtils.MAX_DATE.getFullYear()],
      }
    }
    return VALID
  }

export const requiredSelection =
  (errorKey = 'validation.reporting.filter.required.selection') =>
  (value: []): ValidationOutput => {
    if (value) {
      return value.length > 0 ? VALID : { errorKey }
    }
    return { errorKey }
  }

export const maxListLength =
  (maxLength: number, errorKey = 'validation.list.max.length') =>
  (value: any[]): ValidationOutput => {
    if (!value) {
      return VALID
    }

    const listArray = Array.isArray(value) ? value : [value]

    if (listArray.length > maxLength) {
      return { errorKey, params: [maxLength] }
    }

    return VALID
  }

export const minListLength =
  (minLength: number, errorKey = 'validation.list.min.length') =>
  (value: any[]): ValidationOutput => {
    if (!value) {
      return VALID
    }

    const listArray = Array.isArray(value) ? value : [value]

    if (listArray.length < minLength) {
      return { errorKey, params: [minLength] }
    }

    return VALID
  }
